import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Calendar as CalendarIcon, MapPin, Ruler, Copy, Gift, Share2, Repeat, Pause, Play, Ban } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = {
  pending: { label: "Pending", cls: "bg-status-pending-bg text-status-pending-text" },
  scheduled: { label: "Scheduled", cls: "bg-status-scheduled-bg text-status-scheduled-text" },
  paused: { label: "Paused", cls: "bg-moss text-deep-forest" },
  in_progress: { label: "In progress", cls: "bg-status-progress-bg text-status-progress-text" },
  completed: { label: "Completed", cls: "bg-deep-forest text-warm-sand" },
  cancelled: { label: "Cancelled", cls: "bg-moss text-deep-forest/60" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/bookings");
        setBookings(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalSpent = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + b.total_price, 0);
  const upcoming = bookings.filter((b) => ["scheduled", "pending", "in_progress"].includes(b.status)).length;
  const completed = bookings.filter((b) => b.status === "completed").length;

  const referralCode = user?.referral_code || "";
  const pendingDiscounts = user?.pending_discounts || 0;
  const shareLink = referralCode ? `${window.location.origin}/register?ref=${referralCode}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success("Referral link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "StreetMow",
          text: `Get your lawn mowed by StreetMow. Use my code ${referralCode} when you sign up.`,
          url: shareLink,
        });
      } catch {/* user cancelled */}
    } else {
      copyLink();
    }
  };

  const [cancelTargetId, setCancelTargetId] = useState(null);
  const cancelTarget = bookings.find((b) => b.id === cancelTargetId) || null;

  const pauseBooking = async (id) => {
    try {
      const res = await api.post(`/bookings/${id}/pause`);
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, status: res.data.status } : b));
      toast.success("Booking paused");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause");
    }
  };

  const resumeBooking = async (id) => {
    try {
      const res = await api.post(`/bookings/${id}/resume`);
      setBookings((bs) => bs.map((b) => b.id === id ? { ...b, status: res.data.status } : b));
      toast.success("Booking resumed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume");
    }
  };

  const confirmCancel = async () => {
    if (!cancelTargetId) return;
    try {
      const res = await api.post(`/bookings/${cancelTargetId}/cancel`);
      setBookings((bs) => bs.map((b) => b.id === cancelTargetId ? { ...b, status: "cancelled" } : b));
      const refund = res.data?.refund;
      if (refund?.ok) {
        toast.success("Booking cancelled. Refund issued to your card.");
      } else if (res.data.needs_refund && cancelTarget?.payment_method === "card") {
        toast.success("Booking cancelled. Refund couldn't process automatically — our team will sort it.");
      } else if (res.data.needs_refund) {
        toast.success("Booking cancelled. We'll refund your cash on the next visit.");
      } else {
        toast.success("Booking cancelled");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelTargetId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="label-overline mb-2">— Your dashboard</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-deep-forest tracking-tight">Hello, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <Link to="/book" data-testid="dashboard-new-booking-btn" className="btn-primary"><Plus className="w-4 h-4" /> New booking</Link>
      </div>

      {/* stats */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-deep-forest/10 border border-deep-forest/10">
        <Stat label="Upcoming" value={upcoming} testId="stat-upcoming" />
        <Stat label="Completed" value={completed} testId="stat-completed" />
        <Stat label="Total spent" value={`$${totalSpent.toFixed(2)}`} testId="stat-spent" />
      </div>

      {/* Referral program card */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-px bg-deep-forest/10 border border-deep-forest/10" data-testid="referral-card">
        <div className="lg:col-span-7 bg-deep-forest text-warm-sand p-8">
          <div className="flex items-center gap-2 label-overline text-warm-sand/70"><Gift className="w-3.5 h-3.5" /> Refer a neighbour</div>
          <h3 className="font-serif text-3xl mt-4 italic font-light leading-tight">Get <span className="not-italic text-terracotta">10% off</span> every time<br/>a neighbour signs up.</h3>
          <p className="text-warm-sand/70 text-sm mt-4 max-w-md">Share your code. When they create an account, a 10% discount auto-applies to your next mow.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={copyLink} data-testid="referral-copy-btn" className="btn-primary"><Copy className="w-4 h-4" /> Copy link</button>
            <button onClick={shareNative} data-testid="referral-share-btn" className="border border-warm-sand/40 text-warm-sand px-6 py-3 hover:bg-warm-sand hover:text-deep-forest transition-colors inline-flex items-center gap-2"><Share2 className="w-4 h-4" /> Share</button>
          </div>
        </div>
        <div className="lg:col-span-5 bg-bone-white p-8 flex flex-col justify-between">
          <div>
            <div className="label-overline">Your code</div>
            <div className="font-mono text-4xl tracking-[0.15em] text-deep-forest mt-3" data-testid="referral-code">{referralCode || "—"}</div>
            <div className="text-xs text-deep-forest/50 mt-2 break-all" data-testid="referral-share-link">{shareLink}</div>
          </div>
          <div className="mt-6">
            <div className="label-overline">Discounts earned</div>
            <div className="font-serif text-4xl text-terracotta mt-2" data-testid="referral-pending">{pendingDiscounts}</div>
            <div className="text-xs text-deep-forest/60 mt-1">Auto-applied to your next {pendingDiscounts === 1 ? "booking" : "bookings"}.</div>
          </div>
        </div>
      </div>

      <h2 className="font-serif text-3xl text-deep-forest mt-16 mb-6">Bookings</h2>

      {loading ? (
        <div className="text-deep-forest/60 text-sm" data-testid="bookings-loading">Loading…</div>
      ) : bookings.length === 0 ? (
        <div className="bg-bone-white border border-deep-forest/10 p-12 text-center" data-testid="bookings-empty">
          <div className="font-serif text-3xl text-deep-forest">No bookings yet.</div>
          <p className="text-deep-forest/60 mt-2">Start with your first lawn — front, back, or that strip near the driveway.</p>
          <Link to="/book" data-testid="empty-cta" className="btn-primary inline-flex mt-6">Create your first booking</Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="bookings-list">
          {bookings.map((b) => {
            const s = STATUS_LABEL[b.status] || STATUS_LABEL.pending;
            const canPause = b.status === "scheduled" || b.status === "pending";
            const canResume = b.status === "paused";
            const canCancel = !["completed", "cancelled", "in_progress"].includes(b.status);
            return (
              <div key={b.id} data-testid={`booking-card-${b.id}`} className="bg-bone-white border border-deep-forest/10 p-6 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-5">
                  <div className="flex items-center gap-3">
                    <span data-testid={`booking-status-${b.id}`} className={`text-xs uppercase tracking-widest px-2 py-1 ${s.cls}`}>{s.label}</span>
                    <span className={`text-xs uppercase tracking-widest px-2 py-1 ${b.payment_status === "paid" ? "bg-status-scheduled-bg text-status-scheduled-text" : "bg-status-pending-bg text-status-pending-text"}`}>{b.payment_status}</span>
                  </div>
                  <div className="font-serif text-2xl text-deep-forest mt-3">{b.areas.length} area{b.areas.length > 1 ? "s" : ""} · {b.total_area.toFixed(1)} m²</div>
                  <div className="text-sm text-deep-forest/60 mt-1 flex items-center gap-4">
                    <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {b.address}</span>
                  </div>
                </div>
                <div className="md:col-span-4 flex items-center gap-6 text-sm text-deep-forest/70">
                  <div className="flex items-center gap-2"><CalendarIcon className="w-4 h-4 text-terracotta" /> {b.next_mow_date || b.scheduled_date}</div>
                  <div className="flex items-center gap-2"><Repeat className="w-4 h-4 text-terracotta" /> Every 3 weeks</div>
                </div>
                <div className="md:col-span-3 flex flex-col items-end gap-3">
                  <div className="text-right">
                    <div className="label-overline">Total</div>
                    <div className="font-serif text-3xl text-terracotta" data-testid={`booking-price-${b.id}`}>${b.total_price.toFixed(2)}</div>
                  </div>
                  {(canPause || canResume || canCancel) && (
                    <div className="flex items-center gap-2">
                      {canResume && (
                        <button onClick={() => resumeBooking(b.id)} data-testid={`booking-resume-btn-${b.id}`} className="text-xs uppercase tracking-widest text-deep-forest hover:text-terracotta inline-flex items-center gap-1 px-2 py-1 border border-deep-forest/15">
                          <Play className="w-3 h-3" /> Resume
                        </button>
                      )}
                      {canPause && (
                        <button onClick={() => pauseBooking(b.id)} data-testid={`booking-pause-btn-${b.id}`} className="text-xs uppercase tracking-widest text-deep-forest hover:text-terracotta inline-flex items-center gap-1 px-2 py-1 border border-deep-forest/15">
                          <Pause className="w-3 h-3" /> Pause
                        </button>
                      )}
                      {canCancel && (
                        <button onClick={() => setCancelTargetId(b.id)} data-testid={`booking-cancel-btn-${b.id}`} className="text-xs uppercase tracking-widest text-destructive hover:text-destructive/80 inline-flex items-center gap-1 px-2 py-1 border border-destructive/30">
                          <Ban className="w-3 h-3" /> Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel confirmation */}
      <AlertDialog open={!!cancelTargetId} onOpenChange={(o) => { if (!o) setCancelTargetId(null); }}>
        <AlertDialogContent data-testid="cancel-confirm-dialog" className="bg-bone-white border-deep-forest/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-deep-forest">Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription className="text-deep-forest/70">
              This will stop your upcoming mow{cancelTarget?.scheduled_date ? ` on ${cancelTarget.next_mow_date || cancelTarget.scheduled_date}` : ""} and end the recurring 3-week service.
              {cancelTarget?.payment_status === "paid" && cancelTarget?.payment_method === "card" && (
                <span className="block mt-3 text-deep-forest">Your card payment of <strong>${cancelTarget.total_price.toFixed(2)}</strong> will be refunded to the original card straight away.</span>
              )}
              {cancelTarget?.payment_status === "paid" && cancelTarget?.payment_method === "cash" && (
                <span className="block mt-3 text-deep-forest">We&apos;ll return your <strong>${cancelTarget.total_price.toFixed(2)}</strong> cash next time we&apos;re on your street.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-no">Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} data-testid="cancel-confirm-yes" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Yes, cancel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="bg-bone-white p-8" data-testid={testId}>
      <div className="label-overline">{label}</div>
      <div className="font-serif text-4xl text-deep-forest mt-3">{value}</div>
    </div>
  );
}
